import { Component, OnInit, OnDestroy, PLATFORM_ID, Inject, HostListener } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { BlogService } from '../../../services/blog.service';
import { BlogPost, BlogCategory } from '../../../models/blog.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { SeoService } from '../../../services/seo.service';

@Component({
    selector: 'app-blog-home',
    standalone: true,
    imports: [CommonModule, RouterModule, FormsModule],
    templateUrl: './blog-home.component.html',
    styleUrls: ['./blog-home.component.scss']
})
export class BlogHomeComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    posts: BlogPost[] = [];
    categories: BlogCategory[] = [];
    trending: BlogPost[] = [];

    // Filters
    searchQuery = '';
    selectedCategory = '';
    selectedTag = '';

    // Pagination
    currentPage = 1;
    totalPosts = 0;
    postsPerPage = 9;

    loading = true;
    error = '';

    // Back to Top
    showBackToTop = false;
    private isBrowser = false;

    // Filter bar sticky state
    isFilterSticky = false;

    constructor(
        private blogService: BlogService,
        private router: Router,
        private seoService: SeoService,
        @Inject(PLATFORM_ID) platformId: Object
    ) {
        this.isBrowser = isPlatformBrowser(platformId);
    }

    ngOnInit(): void {
        this.loadPosts();
        this.loadCategories();
        this.loadTrending();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.seoService.removePaginationLinks();
    }

    @HostListener('window:scroll')
    onScroll(): void {
        if (this.isBrowser) {
            this.showBackToTop = window.scrollY > 300;
            this.isFilterSticky = window.scrollY > 220;
        }
    }

    scrollToTop(): void {
        if (this.isBrowser) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    loadPosts(): void {
        this.loading = true;
        this.error = '';

        const offset = (this.currentPage - 1) * this.postsPerPage;

        this.blogService.getPosts({
            limit: this.postsPerPage,
            offset,
            category: this.selectedCategory || undefined,
            tag: this.selectedTag || undefined,
            search: this.searchQuery || undefined
        }).pipe(takeUntil(this.destroy$)).subscribe({
            next: (response: any) => {
                if (response.success) {
                    this.posts = response.data.posts;
                    this.totalPosts = response.data.total;
                    this.updatePaginationLinks();
                }
                this.loading = false;
            },
            error: (err) => {
                this.error = 'Failed to load posts';
                this.loading = false;
            }
        });
    }

    loadCategories(): void {
        this.blogService.getCategories().subscribe({
            next: (response: any) => {
                if (response.success) {
                    this.categories = response.data;
                }
            }
        });
    }

    loadTrending(): void {
        this.blogService.getTrending(5).subscribe({
            next: (response: any) => {
                if (response.success) {
                    this.trending = response.data;
                }
            }
        });
    }

    onSearch(): void {
        this.currentPage = 1;
        this.loadPosts();
    }

    onCategoryChange(slug: string): void {
        this.selectedCategory = slug;
        this.currentPage = 1;
        this.loadPosts();
    }

    onTagClick(tag: string): void {
        this.selectedTag = tag;
        this.currentPage = 1;
        this.loadPosts();
    }

    clearFilters(): void {
        this.searchQuery = '';
        this.selectedCategory = '';
        this.selectedTag = '';
        this.currentPage = 1;
        this.loadPosts();
    }

    get totalPages(): number {
        return Math.ceil(this.totalPosts / this.postsPerPage);
    }

    get featuredPost(): BlogPost | null {
        return this.currentPage === 1 && this.posts.length > 0 ? this.posts[0] : null;
    }

    get gridPosts(): BlogPost[] {
        if (this.currentPage === 1 && this.posts.length > 1) {
            return this.posts.slice(1);
        }
        return this.posts;
    }

    get hasActiveFilters(): boolean {
        return !!(this.searchQuery || this.selectedCategory || this.selectedTag);
    }

    // Insert newsletter CTA between grid rows (after 4th post)
    get newsletterInsertIndex(): number {
        return 4;
    }

    goToPage(page: number): void {
        if (page >= 1 && page <= this.totalPages) {
            this.currentPage = page;
            this.loadPosts();
            if (this.isBrowser) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
    }

    private updatePaginationLinks(): void {
        const baseUrl = 'https://turboindexing.com/blog';
        const prevUrl = this.currentPage > 1 ? `${baseUrl}?page=${this.currentPage - 1}` : null;
        const nextUrl = this.currentPage < this.totalPages ? `${baseUrl}?page=${this.currentPage + 1}` : null;
        this.seoService.setPaginationLinks(prevUrl, nextUrl);
    }

    goToPost(slug: string): void {
        this.router.navigate(['/blog', slug]);
    }

    selectCategory(slug: string): void {
        this.onCategoryChange(slug);
    }

    onFilterChange(): void {
        this.onSearch();
    }

    prevPage(): void {
        this.goToPage(this.currentPage - 1);
    }

    nextPage(): void {
        this.goToPage(this.currentPage + 1);
    }

    formatDate(date: any): string {
        if (!date) return '';
        const d = new Date(date);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    getImageUrl(post: BlogPost): string {
        return post.featured_image?.medium || post.featured_image?.large || '/assets/blog-placeholder.jpg';
    }

    getLargeImageUrl(post: BlogPost): string {
        return post.featured_image?.large || post.featured_image?.original || post.featured_image?.medium || '/assets/blog-placeholder.jpg';
    }

    // Subscribe Logic
    subscribeEmail = '';
    subscribeStatus: 'idle' | 'loading' | 'success' | 'error' = 'idle';
    subscribeMessage = '';

    onSubscribe(): void {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!this.subscribeEmail || !emailRegex.test(this.subscribeEmail)) {
            this.subscribeStatus = 'error';
            this.subscribeMessage = 'Please enter a valid email address.';
            return;
        }

        this.subscribeStatus = 'loading';
        this.blogService.subscribe(this.subscribeEmail).subscribe({
            next: (res) => {
                this.subscribeStatus = 'success';
                this.subscribeMessage = 'Thanks for subscribing!';
                this.subscribeEmail = '';
                setTimeout(() => {
                    this.subscribeStatus = 'idle';
                    this.subscribeMessage = '';
                }, 3000);
            },
            error: (err) => {
                this.subscribeStatus = 'error';
                this.subscribeMessage = err.error?.error || 'Failed to subscribe.';
            }
        });
    }
}
